import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../../../components/dialog";
import { AppButton } from "../../../components/form";
import {
  getHomeGuideCard,
  homeGuideFirstStepId,
  homeGuideStep,
  resolveHomeGuideOption,
  type HomeGuideCardId,
} from "../homeGuideConfig";
import { markHomeGuideCardCompleted } from "../homeGuideStorage";

type Props = {
  open: boolean;
  cardId: HomeGuideCardId | null;
  userId: string;
  workspaceId: string;
  vpnEnabled: boolean;
  onClose: () => void;
  onCompleted: (cardId: HomeGuideCardId) => void;
};

export default function HomeGuideWizardDialog({
  open,
  cardId,
  userId,
  workspaceId,
  vpnEnabled,
  onClose,
  onCompleted,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const card = cardId ? getHomeGuideCard(cardId) : undefined;
  const [stepId, setStepId] = useState<string>("");
  const [history, setHistory] = useState<string[]>([]);

  const activeStepId = stepId || (card ? homeGuideFirstStepId(card) : "");
  const step = card ? homeGuideStep(card, activeStepId) : undefined;

  const stepIndex = useMemo(() => {
    if (!card) return 0;
    const idx = card.steps.findIndex((s) => s.id === activeStepId);
    return idx >= 0 ? idx : 0;
  }, [card, activeStepId]);

  useEffect(() => {
    if (!open || !cardId) return;
    const next = getHomeGuideCard(cardId);
    setStepId(next ? homeGuideFirstStepId(next) : "");
    setHistory([]);
  }, [open, cardId]);

  if (!card || !step) {
    return (
      <AppDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t("home.guide.title")}</AppDialogTitle>
        <AppDialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("home.guide.unavailable")}
          </Typography>
        </AppDialogContent>
        <AppDialogActions>
          <AppDialogCancelButton onClick={onClose}>{t("common.close")}</AppDialogCancelButton>
        </AppDialogActions>
      </AppDialog>
    );
  }

  const finish = (path: string) => {
    markHomeGuideCardCompleted(userId, workspaceId, card.id);
    onCompleted(card.id);
    onClose();
    navigate(path);
  };

  const pick = (optionId: string) => {
    const resolved = resolveHomeGuideOption(card, activeStepId, optionId);
    if (!resolved) return;
    if (resolved.navigate) {
      finish(resolved.navigate);
      return;
    }
    if (resolved.nextStep) {
      setHistory((h) => [...h, activeStepId]);
      setStepId(resolved.nextStep);
    }
  };

  const goBack = () => {
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setStepId(prev);
      return h.slice(0, -1);
    });
  };

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <AppDialogTitle>{t(card.titleKey)}</AppDialogTitle>
      <AppDialogContent>
        <Stack spacing={2}>
          {card.steps.length > 1 && (
            <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 1 }}>
              {card.steps.map((s) => (
                <Step key={s.id}>
                  <StepLabel>{t(s.questionKey)}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            {t(step.questionKey)}
          </Typography>
          {!vpnEnabled && step.options.some((o) => o.requiresVpn) && (
            <Alert severity="info">{t("home.guide.vpnDisabledHint")}</Alert>
          )}
          <Stack spacing={1}>
            {step.options.map((opt) => {
              const disabled = Boolean(opt.requiresVpn && !vpnEnabled);
              return (
                <Card key={opt.id} variant="outlined" sx={{ opacity: disabled ? 0.55 : 1 }}>
                  <CardActionArea disabled={disabled} onClick={() => pick(opt.id)}>
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="body1">{t(opt.labelKey)}</Typography>
                      {disabled && (
                        <Typography variant="caption" color="text.secondary">
                          {t("home.guide.vpnOptionDisabled")}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        </Stack>
      </AppDialogContent>
      <AppDialogActions>
        <AppButton variant="text" onClick={goBack} disabled={history.length === 0}>
          {t("home.guide.back")}
        </AppButton>
        <AppDialogCancelButton onClick={onClose}>{t("common.cancel")}</AppDialogCancelButton>
      </AppDialogActions>
    </AppDialog>
  );
}
